import { IconButtonVariant } from "@inputs/button/IconButtonVariant";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem } from "@mui/material";
import { type Key, useState } from "react";
import type { IResponsiveTabsProps } from ".";

const getSelectedTab = <Value extends Key, Tab>(
  value: IResponsiveTabsProps<Value, Tab>["value"],
  tabs: IResponsiveTabsProps<Value, Tab>["tabs"],
  getValue: IResponsiveTabsProps<Value, Tab>["getValue"]
) => {
  let selectedTab: Tab | null = null;
  const unselectedTabs: Tab[] = [];
  for (const tab of tabs) {
    if (getValue(tab) === value) {
      selectedTab = tab;
    } else {
      unselectedTabs.push(tab);
    }
  }
  return [selectedTab, unselectedTabs] as const;
};

export const ResponsiveTabsReduced = <Value extends Key, Tab>({
  tabs,
  value,
  getLabel,
  getValue,
  onChange,
  renderTab,
}: Omit<IResponsiveTabsProps<Value, Tab>, "breakpoint">) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedTab, restTabs] = getSelectedTab(value, tabs, getValue);

  const handleItemClick = (value: Value) => {
    onChange(value);
    setAnchorEl(null);
  };

  const renderItem = (tab: Tab) => {
    const value = getValue(tab);
    return (
      <MenuItem key={value} onClick={() => handleItemClick(value)}>
        {getLabel(tab)}
      </MenuItem>
    );
  };
  return (
    <>
      {selectedTab && renderTab(selectedTab, () => onChange(getValue(selectedTab)), true)}
      {restTabs.length > 0 && (
        <>
          <IconButtonVariant variant="elevated" onClick={(evt) => setAnchorEl(evt.currentTarget)}>
            <MoreVertIcon color="primary" />
          </IconButtonVariant>
          <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
            {restTabs.map(renderItem)}
          </Menu>
        </>
      )}
    </>
  );
};
